/**
 * Engine: node factory.
 *
 * Owns the canonical shape of a freshly created RelationshipTreeNode and the
 * key-generation scheme (crypto.randomUUID). All other engine modules and the
 * React layer route node creation through here so the persisted shape
 * (treeType, extraInformation defaults, recipientConfig gating on recipient
 * trees) stays in one place. `extraInformation` and `recipientConfig` are
 * deep-cloned from the entity config so each node owns its own mutable
 * substructure (notes array, recipient config object). Firebase Timestamp
 * instances inside notes are immutable and copied by reference. Throws when
 * called with an `entityOrGroupId` that has no entry in `config.entityConfig`
 * — silent fallthrough was producing nodes with no defaults that downstream
 * code could not render correctly. `duplicateNode` reuses the same deep-clone
 * and key scheme to copy an existing node's whole subtree with fresh keys.
 */
import type {
  RelationshipTreeConfig,
  RelationshipTreeNode,
} from '../System.RelationshipTree.types';

export const generateNodeKey = (): string => crypto.randomUUID();

const cloneTemplate = <T>(value: T): T => {
  if (value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) {
    return value.map((item) => cloneTemplate(item)) as unknown as T;
  }
  const proto = Object.getPrototypeOf(value);
  if (proto !== Object.prototype && proto !== null) return value;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    out[k] = cloneTemplate(v);
  }
  return out as T;
};

export const createNode = (
  entityOrGroupId: string,
  config: RelationshipTreeConfig,
  recordId?: string,
  children?: RelationshipTreeNode[],
  key?: string,
): RelationshipTreeNode => {
  const entityConfig = config.entityConfig[entityOrGroupId];
  if (!entityConfig) {
    throw new Error(
      `createNode: no entityConfig for entityOrGroupId '${entityOrGroupId}' in treeType '${config.treeType}'`,
    );
  }
  const extraInformation = cloneTemplate(entityConfig.extraDataTemplate ?? {});
  const base = {
    key: key ?? generateNodeKey(),
    entityOrGroupId,
    entityRecordId: recordId,
    children: children ?? [],
    extraInformation,
  };

  if (config.treeType === 'recipient') {
    return {
      ...base,
      treeType: 'recipient',
      recipientConfig: cloneTemplate(entityConfig.defaultRecipientConfig),
    };
  }

  return {
    ...base,
    treeType: config.treeType,
  };
};

/**
 * Deep-copies a node and its entire subtree, minting a fresh key for every
 * copied node so the duplicate can coexist with the original in the same tree.
 * `entityRecordId` and `entityOrGroupId` are carried over verbatim — a
 * duplicate is a true copy, selected records included. `extraInformation` and
 * `recipientConfig` are deep-cloned so the copy owns its own mutable
 * substructure; Firebase Timestamp instances inside notes are copied by
 * reference (they are immutable).
 */
export const duplicateNode = (
  node: RelationshipTreeNode,
): RelationshipTreeNode => {
  const children = node.children?.map(duplicateNode) ?? [];
  const key = generateNodeKey();
  if (node.treeType === 'recipient') {
    return {
      ...node,
      key,
      children,
      extraInformation: cloneTemplate(node.extraInformation),
      recipientConfig: cloneTemplate(node.recipientConfig),
    };
  }
  return {
    ...node,
    key,
    children,
    extraInformation: cloneTemplate(node.extraInformation),
  };
};
